import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HexColorPicker, HexColorInput } from 'react-colorful';

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

export default function ColorPickerInput({ value, onChange, placeholder = '#C4A962' }: Props) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const color = value || '';

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      {/* Input row */}
      <div className="flex items-center bg-ui-bg border border-ui-border rounded-md focus-within:border-ui-accent focus-within:ring-1 focus-within:ring-ui-accent/30 transition-colors overflow-hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          title="Abrir selector de color"
          className="shrink-0 flex items-center justify-center w-10 h-9 border-r border-ui-border hover:bg-ui-surface transition-colors"
        >
          <motion.span
            key={color}
            initial={{ scale: 0.75, opacity: 0.6 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="block w-5 h-5 rounded-full border border-ui-border"
            style={{ backgroundColor: color || 'transparent' }}
          />
        </button>

        <HexColorInput
          color={color}
          onChange={onChange}
          prefixed
          placeholder={placeholder}
          spellCheck={false}
          className="flex-1 bg-transparent px-3 py-2 text-sm text-ui-text focus:outline-none placeholder:text-ui-text-subtle min-w-0 font-mono"
        />
      </div>

      {/* Popover */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.14, ease: 'easeOut' }}
            className="absolute top-full left-0 mt-1.5 z-50 bg-ui-surface border border-ui-border rounded-lg p-3 shadow-xl"
          >
            <HexColorPicker color={color || '#000000'} onChange={onChange} />
            <div className="mt-2.5 flex items-center gap-2">
              <span className="text-xs text-ui-text-subtle uppercase tracking-wider">HEX</span>
              <HexColorInput
                color={color}
                onChange={onChange}
                prefixed
                spellCheck={false}
                className="flex-1 bg-ui-bg border border-ui-border rounded-md px-2 py-1.5 text-sm text-ui-text font-mono focus:outline-none focus:border-ui-accent transition-colors"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
